import type {
  ReportLayoutWidgetDto,
  ReportTemplateFieldDto,
  ReportTemplateLayoutDto,
} from "../../api/reportTemplateService"
import { isLikelyImageUrl, toWidgetTitle } from "./editorUtils"

export type ReportDesignerValidationSeverity = "error" | "warning"
export type ReportDesignerValidationCategory = "datasource" | "widget"

export interface ReportDesignerValidationIssue {
  id: string
  severity: ReportDesignerValidationSeverity
  category: ReportDesignerValidationCategory
  targetId?: string
  message: string
}

type ValidateReportDesignerParams = {
  layout: ReportTemplateLayoutDto
  availableFields: ReportTemplateFieldDto[]
}

type WidgetEntry = {
  widget: ReportLayoutWidgetDto
  section: "body" | "header" | "footer"
}

const normalizeKey = (value: string) => value.trim().toLowerCase()

const getWidgetSectionLabel = (section: WidgetEntry["section"]) => {
  if (section === "header") return "Header"
  if (section === "footer") return "Footer"
  return "Body"
}

const getWidgetLabel = (entry: WidgetEntry) => `${toWidgetTitle(entry.widget)} (${getWidgetSectionLabel(entry.section)})`

export function validateReportDesigner({
  layout,
  availableFields,
}: ValidateReportDesignerParams): ReportDesignerValidationIssue[] {
  const issues: ReportDesignerValidationIssue[] = []
  const templateFieldKeys = new Set(availableFields.map((field) => normalizeKey(field.fieldKey)))
  const datasources = Array.isArray(layout.datasources) ? layout.datasources : []
  const datasourceMap = new Map(datasources.map((item) => [item.id, item]))
  const referencedDatasourceIds = new Set<string>()
  const widgets: WidgetEntry[] = [
    ...(layout.widgets ?? []).map((widget) => ({ widget, section: "body" as const })),
    ...((layout.headerSection?.widgets ?? []).map((widget) => ({ widget, section: "header" as const }))),
    ...((layout.footerSection?.widgets ?? []).map((widget) => ({ widget, section: "footer" as const }))),
  ]

  widgets.forEach(({ widget }) => {
    const datasourceId = String(widget.props?.datasourceId || "").trim()
    if (datasourceId) referencedDatasourceIds.add(datasourceId)
  })

  datasources.forEach((datasource, index) => {
    const label = datasource.name || datasource.objectName || `Datasource ${index + 1}`
    if (!String(datasource.objectName || "").trim()) {
      issues.push({
        id: `datasource-${datasource.id}-missing-view`,
        severity: "error",
        category: "datasource",
        targetId: datasource.id,
        message: `Datasource "${label}" has no view selected.`,
      })
    }

    ;(datasource.bindings ?? []).forEach((binding, bindingIndex) => {
      if (!String(binding.columnName || "").trim() || !String(binding.contextKey || "").trim()) {
        issues.push({
          id: `datasource-${datasource.id}-binding-${binding.id || bindingIndex + 1}-incomplete`,
          severity: "error",
          category: "datasource",
          targetId: datasource.id,
          message: `Datasource "${label}" has an incomplete binding row.`,
        })
      }
    })

    if (referencedDatasourceIds.has(datasource.id) && (datasource.fields ?? []).length === 0) {
      issues.push({
        id: `datasource-${datasource.id}-missing-schema`,
        severity: "warning",
        category: "datasource",
        targetId: datasource.id,
        message: `Datasource "${label}" is already used by widgets but has no schema loaded yet. Run "Test Source" to expose columns.`,
      })
    }
  })

  const validateTemplateFieldReference = (
    entry: WidgetEntry,
    fieldKey: string,
    labelPrefix: string,
  ) => {
    const safeFieldKey = String(fieldKey || "").trim()
    if (!safeFieldKey) {
      issues.push({
        id: `widget-${entry.widget.id}-${labelPrefix}-missing-field`,
        severity: "error",
        category: "widget",
        targetId: entry.widget.id,
        message: `${labelPrefix} "${getWidgetLabel(entry)}" has no field selected.`,
      })
      return
    }

    if (!templateFieldKeys.has(normalizeKey(safeFieldKey))) {
      issues.push({
        id: `widget-${entry.widget.id}-${labelPrefix}-unknown-field`,
        severity: "error",
        category: "widget",
        targetId: entry.widget.id,
        message: `${labelPrefix} "${getWidgetLabel(entry)}" references unknown template field "${safeFieldKey}".`,
      })
    }
  }

  const validateDatasourceFieldReference = (
    entry: WidgetEntry,
    datasourceId: string,
    fieldKey: string,
    widgetLabel: string,
  ) => {
    const datasource = datasourceMap.get(datasourceId)
    if (!datasource) {
      issues.push({
        id: `widget-${entry.widget.id}-missing-datasource`,
        severity: "error",
        category: "widget",
        targetId: entry.widget.id,
        message: `${widgetLabel} "${getWidgetLabel(entry)}" references datasource "${datasourceId}" that no longer exists.`,
      })
      return
    }

    if ((datasource.fields ?? []).length === 0) {
      issues.push({
        id: `widget-${entry.widget.id}-datasource-schema-pending`,
        severity: "warning",
        category: "widget",
        targetId: entry.widget.id,
        message: `${widgetLabel} "${getWidgetLabel(entry)}" uses datasource "${datasource.name || datasource.objectName}" but that datasource schema has not been loaded yet.`,
      })
      return
    }

    const safeFieldKey = String(fieldKey || "").trim()
    if (!safeFieldKey) {
      issues.push({
        id: `widget-${entry.widget.id}-datasource-field-missing`,
        severity: "error",
        category: "widget",
        targetId: entry.widget.id,
        message: `${widgetLabel} "${getWidgetLabel(entry)}" has no datasource field selected.`,
      })
      return
    }

    const hasField = (datasource.fields ?? []).some((item) => normalizeKey(item.fieldKey) === normalizeKey(safeFieldKey))
    if (!hasField) {
      issues.push({
        id: `widget-${entry.widget.id}-datasource-field-invalid`,
        severity: "error",
        category: "widget",
        targetId: entry.widget.id,
        message: `${widgetLabel} "${getWidgetLabel(entry)}" references field "${safeFieldKey}" that is not in datasource "${datasource.name || datasource.objectName}".`,
      })
    }
  }

  widgets.forEach((entry) => {
    const { widget } = entry
    const widgetProps = widget.props ?? {}
    const datasourceId = String(widgetProps.datasourceId || "").trim()

    if (widget.type === "field") {
      const fieldKey = String(widgetProps.fieldKey || "").trim()
      if (datasourceId) {
        const datasource = datasourceMap.get(datasourceId)
        if (datasource && datasource.cardinality !== "record") {
          issues.push({
            id: `widget-${widget.id}-field-cardinality`,
            severity: "error",
            category: "widget",
            targetId: widget.id,
            message: `Field widget "${getWidgetLabel(entry)}" is bound to datasource "${datasource.name || datasource.objectName}" with Table cardinality. Field widgets require Record datasources.`,
          })
        }
        validateDatasourceFieldReference(entry, datasourceId, fieldKey, "Field widget")
      } else {
        validateTemplateFieldReference(entry, fieldKey, "Field widget")
      }
    }

    if (widget.type === "image") {
      const bindingType = String(widgetProps.bindingType || "static")
      if (bindingType === "field") {
        const fieldKey = String(widgetProps.fieldKey || "").trim()
        if (datasourceId) {
          validateDatasourceFieldReference(entry, datasourceId, fieldKey, "Image widget")
        } else {
          validateTemplateFieldReference(entry, fieldKey, "Image widget")
        }
      } else {
        const staticUrl = String(widgetProps.staticUrl || "").trim()
        if (staticUrl && !isLikelyImageUrl(staticUrl)) {
          issues.push({
            id: `widget-${widget.id}-image-url-invalid`,
            severity: "warning",
            category: "widget",
            targetId: widget.id,
            message: `Image widget "${getWidgetLabel(entry)}" has a static URL that does not look like an image path or URL.`,
          })
        }
      }
    }

    if (widget.type === "table") {
      const tableSource = String(widgetProps.tableSource || "reportRows")
      const columnKeys = String(widgetProps.columns || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)

      if (columnKeys.length === 0) {
        issues.push({
          id: `widget-${widget.id}-table-columns-empty`,
          severity: "warning",
          category: "widget",
          targetId: widget.id,
          message: `Table widget "${getWidgetLabel(entry)}" has no columns selected.`,
        })
      }

      if (tableSource === "datasource") {
        const datasource = datasourceMap.get(datasourceId)
        if (!datasourceId) {
          issues.push({
            id: `widget-${widget.id}-table-datasource-missing`,
            severity: "error",
            category: "widget",
            targetId: widget.id,
            message: `Table widget "${getWidgetLabel(entry)}" is set to Report Data Source but no datasource is selected.`,
          })
          return
        }

        if (!datasource) {
          issues.push({
            id: `widget-${widget.id}-table-datasource-invalid`,
            severity: "error",
            category: "widget",
            targetId: widget.id,
            message: `Table widget "${getWidgetLabel(entry)}" references datasource "${datasourceId}" that no longer exists.`,
          })
          return
        }

        if (datasource.cardinality !== "table") {
          issues.push({
            id: `widget-${widget.id}-table-cardinality`,
            severity: "error",
            category: "widget",
            targetId: widget.id,
            message: `Table widget "${getWidgetLabel(entry)}" is bound to datasource "${datasource.name || datasource.objectName}" with Record cardinality. Table widgets require Table datasources.`,
          })
        }

        if ((datasource.fields ?? []).length > 0) {
          const datasourceFieldKeys = new Set((datasource.fields ?? []).map((item) => normalizeKey(item.fieldKey)))
          columnKeys
            .filter((key) => normalizeKey(key) !== "__rowno")
            .forEach((columnKey) => {
              if (!datasourceFieldKeys.has(normalizeKey(columnKey))) {
                issues.push({
                  id: `widget-${widget.id}-table-column-${columnKey}`,
                  severity: "error",
                  category: "widget",
                  targetId: widget.id,
                  message: `Table widget "${getWidgetLabel(entry)}" references datasource column "${columnKey}" that is not available in "${datasource.name || datasource.objectName}".`,
                })
              }
            })
        } else {
          issues.push({
            id: `widget-${widget.id}-table-datasource-schema-pending`,
            severity: "warning",
            category: "widget",
            targetId: widget.id,
            message: `Table widget "${getWidgetLabel(entry)}" is bound to datasource "${datasource.name || datasource.objectName}" but the datasource schema has not been loaded yet.`,
          })
        }
      }

      if (tableSource === "reportRows") {
        columnKeys
          .filter((key) => normalizeKey(key) !== "__rowno")
          .forEach((columnKey) => {
            if (!templateFieldKeys.has(normalizeKey(columnKey))) {
              issues.push({
                id: `widget-${widget.id}-report-row-column-${columnKey}`,
                severity: "error",
                category: "widget",
                targetId: widget.id,
                message: `Table widget "${getWidgetLabel(entry)}" references unknown report field "${columnKey}".`,
              })
            }
          })
      }
    }
  })

  return issues
}
